import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { EventSession, Id, Room } from "../../interfaces/types";
import {DeleteOutline, MeetingRoom} from '@mui/icons-material';
import {Groups} from '@mui/icons-material';
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities"
import { FC, useMemo, useState } from "react";
import EventSessionContainer from "./eventSessionContainer";
import { useCustomThemeContext } from "../../providers/customThemeProvider";

interface Props {
    room: Room;
    eventSessions: EventSession[];
    deleteRoom: (roomId: Id) => void;
    updateRoomName: (roomId: Id, name: string) => void;
    updateRoomCapacity: (roomId: Id, capacity: number) => void;
    deleteEventSessionFromRoom: (eventSessionId: Id) => void;
    updateSessionTitle: (eventSessionId: Id, title: string) => void;
    updateSessionDuration: (eventSessionId: Id, duration: number) => void;
}

const RoomContainer: FC<Props> = (props) => {
    const { theme } = useCustomThemeContext();  
    const { room, eventSessions,
        deleteRoom, updateRoomName, updateRoomCapacity, deleteEventSessionFromRoom,
        updateSessionTitle, updateSessionDuration } = props;
    const [editNameMode, setEditNameMode] = useState(false);
    const [editCapacityMode, setEditCapacityMode] = useState(false);
    const eventSessionIds = useMemo(() => eventSessions.map((eventSession) => eventSession.id), [eventSessions]);


    const {setNodeRef, attributes, listeners,
        transform, transition, isDragging} = useSortable({
        id: room.id,
        data:{
            type: "Room",
            room,
        },
        disabled: editNameMode || editCapacityMode,
    });

    const style = {
        transition,
        transform: CSS.Transform.toString(transform),
    };

    if(isDragging){
        return <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            border={2}
            borderRadius={2}
            borderColor={theme.palette.dragBorderColor.main}
            style={{ ...style, opacity: 0.5 }}
            width={"45%"}
            height={"auto"}
            ref={setNodeRef}>
        </Box>;
    }

  return (
    <Box className="RoomContainer"
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        border={2}
        borderRadius={2}
        borderColor={theme.palette.primary.main}
        boxShadow={2}
        width={"45%"}
        height={"auto"}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}>
        <Box className="RoomHeader"
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            borderBottom={2}
            borderColor={theme.palette.primary.main}
            width="100%">
            <Box className="RoomName"
                    textTransform={"uppercase"}
                    fontSize={30}
                    fontWeight="bold"
                    width="45%"
                    color={theme.palette.primary.main}
                    onClick={() => setEditNameMode(true)}
                    sx={{ml:1, mr:1}}>
                {!editNameMode && room.name}
                {editNameMode && <TextField autoFocus
                    value={room.name}
                    onBlur={() => setEditNameMode(false)}
                    onKeyDown={(e) => {
                        if(e.key !== "Enter") return;
                        setEditNameMode(false);
                    }
                }
                slotProps={{
                    input: {startAdornment: <InputAdornment position="start"><MeetingRoom/></InputAdornment>},
                }}
                onChange={(e) => updateRoomName(room.id, e.target.value)}
                />}
            </Box>
            <Box className="RoomCapacity"
                    fontSize={20}
                    fontWeight="bold"
                    width="45%"
                    color={theme.palette.primary.main}
                    onClick={() => setEditCapacityMode(true)}
                    sx={{ml:1, mr:1}}>
                {!editCapacityMode && 
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Groups /> {room.capacity}
                    </div>}
                {editCapacityMode && <TextField autoFocus
                    value={room.capacity}
                    type="number"
                    onBlur={() => setEditCapacityMode(false)}
                    onKeyDown={(e) => {
                        if(e.key !== "Enter") return;
                            setEditCapacityMode(false);
                        }
                    }
                    slotProps={{
                        input: {startAdornment: <InputAdornment position="start"><Groups/></InputAdornment>},
                    }}
                    onChange={(e) => updateRoomCapacity(room.id, Number(e.target.value))}
                />}
            </Box>
            <Button 
                sx={{width:"10%"}}
                variant="outlined" 
                color="error"	 
                className="RoomDeleteButton"
                onClick={() => deleteRoom(room.id)}>
                <DeleteOutline/>
            </Button>  
        </Box>

        <Box className="RoomSessions"
                display={"flex"}
                flexDirection={"column"}
                flexGrow={1}
                marginTop={"10px"}
                marginBottom={"10px"}
                width="100%">
            <SortableContext items={eventSessionIds}>
                {eventSessions.map((eventSession) => (
                    <EventSessionContainer 
                        key={eventSession.id} 
                        eventSession={eventSession}
                        deleteEventSession={deleteEventSessionFromRoom}
                        updateSessionTitle={updateSessionTitle}
                        updateSessionDuration={updateSessionDuration}
                        />
                ))}
            </SortableContext>
        </Box>
    </Box>
  )
}

export default RoomContainer;